/**
 * S-26 Cooldown screen (V2 superreject-shield) — the super-rejected state,
 * v3 "AFTER-DARK ARCADE": a funereal violet scene, not an error page.
 *
 * The title stamps down like a verdict, the tabular countdown burns violet
 * inside a CRT-tinged glass panel, and a slow candle-light veil breathes
 * behind everything. Waiting is staged; nothing here apologizes.
 *
 * The SM-2 anonymity & recourse invariant stays untouched AND untheatrical:
 * "Report this Super Reject" and "Block whoever did this" live in a calm,
 * sober card (safety surfaces never joke, never glow) — both resolved
 * SERVER-SIDE from the sr_id; the sender is never named here or anywhere.
 *
 * Variant rule: /me.cooldown carries sr_id only for a LANDED shot against
 * you; a cooldown without one is your own reversed SR (©cooldown.reversed_body
 * — no recourse CTAs: you fired it, you know exactly who at).
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { formatCopy, resolveCopy } from "@humble/shared";
import { Button, Card, Mascot, stampVariants, toast } from "../../design/index.js";
import { api, isApiError } from "../../lib/api.js";
import { ws } from "../../lib/ws.js";
import { useCountdown } from "./use-countdown.js";
import "./cooldown.css";

interface CooldownState {
  active: boolean;
  expires_at?: string;
  sr_id?: string;
}

export function CooldownScreen() {
  const [cooldown, setCooldown] = useState<CooldownState | null>(null);
  const [loading, setLoading] = useState(true);
  const [reported, setReported] = useState(false);
  const [blocked, setBlocked] = useState(false);
  const [confirmBlock, setConfirmBlock] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const me = await api.get("/me");
      setCooldown((me.cooldown as CooldownState | undefined) ?? { active: false });
    } catch (err) {
      if (isApiError(err)) toast(err.message, "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
    ws.ensureConnected();
    const offEnded = ws.on("cooldown.ended", () => void refresh());
    const offStarted = ws.on("cooldown.started", () => void refresh());
    return () => {
      offEnded();
      offStarted();
    };
  }, [refresh]);

  const remaining = useCountdown(cooldown?.active ? (cooldown.expires_at ?? null) : null);

  useEffect(() => {
    // Countdown hit zero before the server sweep pinged us — recheck.
    if (cooldown?.active && remaining === "00:00:00") {
      const t = setTimeout(() => void refresh(), 1500);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [cooldown, remaining, refresh]);

  const srId = cooldown?.sr_id;

  async function report() {
    if (!srId || reported) return;
    try {
      await api.post("/reports", {
        body: { category: "harassment", sr_id: srId },
      });
      setReported(true);
      toast(resolveCopy("safety.report_ack") ?? "Report received.", "success");
    } catch (err) {
      if (isApiError(err)) toast(err.message, "error");
    }
  }

  async function block() {
    if (!srId || blocked) return;
    try {
      await api.post("/blocks", { body: { sr_id: srId } });
      setBlocked(true);
      setConfirmBlock(false);
      toast(resolveCopy("cooldown.block_cta_sub") ?? "Blocked.", "success");
    } catch (err) {
      if (isApiError(err)) toast(err.message, "error");
    }
  }

  if (loading) {
    return (
      <div className="cooldown cooldown--center">
        <Mascot mood="loading" />
      </div>
    );
  }

  if (!cooldown?.active) {
    return (
      <div className="cooldown cooldown--center">
        <Card padding="lg" className="cooldown-scene">
          <Mascot mood="idle" />
          <h1 className="type-display-3">No timeout here</h1>
          <p className="text-dim">{resolveCopy("cooldown.back_notification")}</p>
          <Link to="/deck" className="btn btn--primary" style={{ marginTop: 10 }}>
            Back to the deck
          </Link>
        </Card>
      </div>
    );
  }

  const reversed = !srId;
  const body = reversed
    ? formatCopy(resolveCopy("cooldown.reversed_body") ?? "", { "hh:mm": remaining })
    : formatCopy(resolveCopy("cooldown.body") ?? "", { "hh:mm": remaining });

  return (
    <div className="cooldown">
      {/* the funereal violet atmosphere — breathes slowly, decorates nothing */}
      <div className="cooldown__veil" aria-hidden="true" />
      <div className="cooldown__stage">
        <motion.h1
          className="type-display-2 cooldown__title"
          variants={stampVariants}
          initial="initial"
          animate="enter"
        >
          {resolveCopy("cooldown.title")}
        </motion.h1>

        <div className="cooldown__panel crt">
          <div className="cooldown__timer type-numeric" role="timer" aria-label="Cooldown remaining">
            {remaining}
          </div>
          <div className="cooldown__caption type-editorial">
            {resolveCopy("cooldown.timer_caption")}
          </div>
        </div>

        <p className="cooldown__body text-dim">{body}</p>

        {!reversed && (
          /* SM-2 recourse — a calm, sober card. The violet stops here. */
          <Card padding="lg" className="cooldown__recourse">
            <h2 className="h-caps cooldown__recourse-title">Your move</h2>
            <div className="cooldown__cta">
              <Button variant="primary" block disabled={reported} onClick={() => void report()}>
                {reported ? "Reported. A human will look." : (resolveCopy("cooldown.report_cta") ?? "Report")}
              </Button>
              <p className="text-faint cooldown__sub">{resolveCopy("cooldown.report_cta_sub")}</p>
            </div>
            <div className="cooldown__cta">
              {confirmBlock ? (
                <>
                  <p className="text-dim cooldown__sub">{resolveCopy("safety.block_anonymous_confirm")}</p>
                  <div className="cooldown__confirm-row">
                    <Button variant="danger" block onClick={() => void block()}>
                      Block them
                    </Button>
                    <Button variant="ghost" block onClick={() => setConfirmBlock(false)}>
                      Not now
                    </Button>
                  </div>
                </>
              ) : (
                <Button variant="ghost" block disabled={blocked} onClick={() => setConfirmBlock(true)}>
                  {blocked ? "Blocked. Forever." : (resolveCopy("cooldown.block_cta") ?? "Block")}
                </Button>
              )}
              <p className="text-faint cooldown__sub">{resolveCopy("cooldown.block_cta_sub")}</p>
            </div>
          </Card>
        )}

        <Card padding="md" className="cooldown__open">
          <p className="text-dim">{resolveCopy("cooldown.whats_open")}</p>
          <div className="cooldown__links">
            <Link to="/matches" className="btn btn--sm">
              Conversations
            </Link>
            <Link to="/store" className="btn btn--sm">
              Store
            </Link>
            <Link to="/shield" className="btn btn--sm">
              Arsenal
            </Link>
          </div>
        </Card>

        <p className="cooldown__pitch text-faint">
          {resolveCopy("cooldown.shield_pitch")}{" "}
          <Link to="/shield" className="cooldown__pitch-link">
            See the Shield
          </Link>
        </p>
      </div>
    </div>
  );
}
